import {
  addMonths,
  endOfDay,
  endOfMonth,
  format,
  isBefore,
  isSameMonth,
  isToday,
  isWithinInterval,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfTomorrow,
  subMonths,
} from 'date-fns';
import {
  CSSProperties,
  FunctionComponent,
  memo,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import sprite from '../../../assets/icons/sprite.svg';
import { ChannelContext } from '../../../context';
import {
  EventActivityStatus,
  EventCalendarPreview,
  EventUserStatus,
  useEventsCalendarQuery,
} from '../../../services';
import {
  cookieOptions,
  EVENTS_FEED_ADMIN_COOKIE_KEY,
  EVENTS_FEED_COOKIE_KEY,
  getDateFnsLocale,
  isAdminLayout,
  layoutPath,
  optArrItem,
  useMonthGrid,
} from '../../../shared';

import classNames from 'classnames';
import classes from './EventsCalendar.module.scss';

export interface ConfigEventsCalendarWidget {
  backgroundColor?: string;
  textColor?: string;
  accentColor?: string;
  highlightBackgroundColor?: string;
  highlightTextColor?: string;
}

interface EventsCalendarProps {
  config?: ConfigEventsCalendarWidget;
}

export const EventsCalendar: FunctionComponent<EventsCalendarProps> = memo(({ config }) => {
  const { t } = useTranslation('', { keyPrefix: 'eventsCalendar' });

  const navigate = useNavigate();

  const { id: channelId } = useContext(ChannelContext).channel;

  const cookieKey = isAdminLayout() ? EVENTS_FEED_ADMIN_COOKIE_KEY : EVENTS_FEED_COOKIE_KEY;

  const [cookies, setCookie] = useCookies([cookieKey]);

  const [viewDate, setViewDate] = useState<Date>(new Date());

  const { data } = useEventsCalendarQuery({
    channelId,
    filters: {
      from: startOfDay(startOfMonth(viewDate)).toISOString(),
      to: endOfDay(endOfMonth(viewDate)).toISOString(),
    },
  });

  const events = useMemo(() => data ?? [], [data]);

  const { calendarDays, weekDayLabels } = useMonthGrid(viewDate);

  const buildEventDaySet = useCallback(
    (events: EventCalendarPreview[]) => {
      const days = new Set<string>();
      events.forEach(({ startAt, endAt }) => {
        const interval = {
          start: startOfDay(parseISO(startAt)),
          end: endOfDay(endAt ? parseISO(endAt) : parseISO(startAt)),
        };
        calendarDays.forEach((day) => {
          if (isWithinInterval(day, interval)) {
            days.add(format(day, 'yyyy-MM-dd'));
          }
        });
      });
      return days;
    },
    [calendarDays]
  );

  const eventDays = useMemo(() => buildEventDaySet(events), [buildEventDaySet, events]);

  const confirmedDays = useMemo(
    () =>
      buildEventDaySet(
        events.filter(({ participantStatus }: EventCalendarPreview) =>
          [EventUserStatus.CONFIRMED, EventUserStatus.HOST].includes(participantStatus)
        )
      ),
    [buildEventDaySet, events]
  );

  const handleDayClick = useCallback(
    (day: Date) => {
      const dateRange = { from: startOfDay(day).toISOString(), to: endOfDay(day).toISOString() };

      const isPastOrToday = isBefore(day, startOfTomorrow());

      setCookie(
        cookieKey,
        {
          ...cookies[cookieKey],
          requestFilters: {
            dateRange,
            ...(isPastOrToday && { activityStatuses: [EventActivityStatus.PAST] }),
          },
          filter: [
            { groupId: 'date', groupItems: [{ id: 'custom-range', value: dateRange }] },
            ...optArrItem(isPastOrToday, {
              groupId: 'activityStatuses',
              groupItems: [{ id: EventActivityStatus.PAST, value: EventActivityStatus.PAST }],
            }),
          ],
        },
        cookieOptions()
      );
      navigate(layoutPath('/events'));
    },
    [cookieKey, cookies, setCookie, navigate]
  );

  const handlePrevMonth = useCallback(() => {
    setViewDate((prevViewDate) => subMonths(prevViewDate, 1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setViewDate((prevViewDate) => addMonths(prevViewDate, 1));
  }, []);

  return (
    <div
      className={classes['events-calendar']}
      style={
        {
          '--events-calendar-background-color': config?.backgroundColor,
          '--events-calendar-text-color': config?.textColor,
          '--events-calendar-accent-color': config?.accentColor,
          '--events-calendar-highlight-background-color': config?.highlightBackgroundColor,
          '--events-calendar-highlight-text-color': config?.highlightTextColor,
        } as CSSProperties
      }
    >
      <div className={classes['events-calendar__header']}>
        <button
          className={classes['events-calendar__header-arrow']}
          onClick={handlePrevMonth}
          aria-label={t('previous-month')}
        >
          <svg className={classes['events-calendar__header-arrow-icon']}>
            <use href={`${sprite}#left-arrow`} />
          </svg>
        </button>
        <span className={classes['events-calendar__header-title']}>
          {format(viewDate, 'MMMM yyyy', { locale: getDateFnsLocale() })}
        </span>
        <button
          className={classes['events-calendar__header-arrow']}
          onClick={handleNextMonth}
          aria-label={t('next-month')}
        >
          <svg className={classes['events-calendar__header-arrow-icon']}>
            <use href={`${sprite}#right-arrow`} />
          </svg>
        </button>
      </div>

      <div className={classes['events-calendar__weekdays']}>
        {weekDayLabels.map((label) => (
          <div key={label} className={classes['events-calendar__weekday']}>
            {label}
          </div>
        ))}
      </div>

      <div className={classes['events-calendar__grid']}>
        {calendarDays.map((day) => {
          const key = format(day, 'yyyy-MM-dd');

          if (!isSameMonth(day, viewDate)) {
            return (
              <div
                key={key}
                className={classNames(
                  classes['events-calendar__day'],
                  classes['events-calendar__day--disabled']
                )}
              >
                <span className={classes['events-calendar__day-number']}>{format(day, 'd')}</span>
              </div>
            );
          }

          const isConfirmed = confirmedDays.has(key);
          const isCurrentDay = isToday(day);

          return (
            <button
              key={key}
              type={'button'}
              className={classNames(classes['events-calendar__day'], {
                [classes['events-calendar__day--today']]: isCurrentDay,
                [classes['events-calendar__day--saved']]: isConfirmed,
              })}
              aria-label={format(day, 'PPPP', { locale: getDateFnsLocale() })}
              aria-current={isCurrentDay && 'date'}
              onClick={() => handleDayClick(day)}
            >
              <span className={classes['events-calendar__day-number']}>{format(day, 'd')}</span>
              {eventDays.has(key) && <span className={classes['events-calendar__day-dot']} />}
              {isConfirmed && (
                <span className={classes['events-calendar__day-badge']}>
                  <svg className={classes['events-calendar__day-badge-icon']}>
                    <use href={`${sprite}#checkmark`} />
                  </svg>
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
});
